import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, MapPin } from "lucide-react";
import { GustoLogo } from "@/components/GustoLogo";
import GustoLoadingOverlay from "@/components/GustoLoadingOverlay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { toast } from "sonner";
import { motion } from "framer-motion";

const RegisterPage = () => {
  const [pseudo, setPseudo] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await register(pseudo, email, password, postalCode);
      toast.success("Compte créé avec succès !");
      navigate("/onboarding");
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <GustoLoadingOverlay show={loading} message="Création de votre espace..." />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-charcoal mb-6">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <GustoLogo size="xl" />
          </Link>
          <p className="text-muted-foreground mt-2">Rejoignez la communauté des Chefs Impro</p>
        </div>

        <Card className="shadow-lg border-border/50" data-testid="register-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Créer un compte</CardTitle>
            <CardDescription>Devenez un Chef Impro et partagez vos créations</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pseudo">Pseudo (votre nom de Chef)</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="pseudo"
                    type="text"
                    placeholder="Jeremy"
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="pseudo-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Vous apparaîtrez comme "Chef(fe) {pseudo || 'Votre pseudo'}"</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="chef@cuisine.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 rounded-xl"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="postalCode">Code postal</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="postalCode"
                    type="text"
                    placeholder="13800"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="pl-10 h-12 rounded-xl"
                    required
                    maxLength={5}
                    data-testid="postal-code-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Pour afficher les commerces autour de vous</p>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 h-12 rounded-full shadow-md"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? "Création..." : "Créer mon compte"}
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                Déjà un compte ?{" "}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Se connecter
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
